import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Config-driven inline-editable grid for Site Diary line items.
 * columns: [{ key, label, type: 'text'|'number'|'select'|'textarea', options?, width?, placeholder? }]
 */
const inputCls =
  'w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none';

function Cell({ col, value, onChange }) {
  if (col.type === 'select') {
    return (
      <select className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {col.options.map((o) => (
          <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
            {typeof o === 'string' ? o.replace(/_/g, ' ') : o.label}
          </option>
        ))}
      </select>
    );
  }
  if (col.type === 'textarea') {
    return (
      <textarea className={inputCls} rows={1} value={value ?? ''} placeholder={col.placeholder}
        onChange={(e) => onChange(e.target.value)} />
    );
  }
  return (
    <input
      className={inputCls}
      type={col.type === 'number' ? 'number' : col.type === 'datetime' ? 'datetime-local' : 'text'}
      value={value ?? ''}
      placeholder={col.placeholder}
      onChange={(e) => onChange(col.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
    />
  );
}

export default function LineItemGrid({
  title, icon: Icon, columns, rows = [], editable, onAdd, onUpdate, onDelete, summary, testId,
}) {
  const emptyRow = () => columns.reduce((a, c) => ({ ...a, [c.key]: c.default ?? '' }), {});
  const [newRow, setNewRow] = useState(emptyRow());
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    const missing = columns.filter((c) => c.required && (newRow[c.key] === '' || newRow[c.key] == null));
    if (missing.length) { toast.error(`Please fill: ${missing.map((m) => m.label).join(', ')}`); return; }
    setBusy(true);
    try {
      await onAdd(newRow);
      setNewRow(emptyRow());
      setAdding(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add row');
    } finally { setBusy(false); }
  };

  const handleSaveEdit = async (id) => {
    setBusy(true);
    try {
      await onUpdate(id, editRow);
      setEditingId(null); setEditRow({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 text-slate-800 font-semibold">
          {Icon && <Icon className="w-4 h-4 text-teal-600" />}
          {title}
          <span className="text-xs font-normal text-slate-400">({rows.length})</span>
        </div>
        {editable && !adding && (
          <button onClick={() => setAdding(true)} data-testid={`${testId}-add-btn`}
            className="inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-900 font-medium">
            <Plus className="w-4 h-4" /> Add
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 bg-slate-50">
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>
              ))}
              {editable && <th className="px-3 py-2 w-20"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !adding && (
              <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-slate-400">No entries yet.</td></tr>
            )}
            {rows.map((row) => {
              const isEditing = editingId === row.id;
              return (
                <tr key={row.id} className="border-t border-slate-100 align-top">
                  {columns.map((c) => (
                    <td key={c.key} className="px-3 py-2">
                      {isEditing && !c.readOnly ? (
                        <Cell col={c} value={editRow[c.key]} onChange={(v) => setEditRow((p) => ({ ...p, [c.key]: v }))} />
                      ) : (
                        <span className="text-slate-700">
                          {c.render ? c.render(row) : (row[c.key] === '' || row[c.key] == null ? '—' : String(row[c.key]).replace(/_/g, ' '))}
                        </span>
                      )}
                    </td>
                  ))}
                  {editable && (
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <div className="flex gap-1">
                          <button disabled={busy} onClick={() => handleSaveEdit(row.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                          <button onClick={() => { setEditingId(null); setEditRow({}); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => { setEditingId(row.id); setEditRow(row); }} className="p-1 text-slate-500 hover:bg-slate-100 rounded" data-testid={`${testId}-edit`}><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => onDelete(row.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`${testId}-delete`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {adding && (
              <tr className="border-t-2 border-teal-200 bg-teal-50/40">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2">
                    <Cell col={c} value={newRow[c.key]} onChange={(v) => setNewRow((p) => ({ ...p, [c.key]: v }))} />
                  </td>
                ))}
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button disabled={busy} onClick={handleAdd} data-testid={`${testId}-save-new`} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="w-4 h-4" /></button>
                    <button onClick={() => { setAdding(false); setNewRow(emptyRow()); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          {summary && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-700">
                {summary(rows)}
                {editable && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
